// web/admin/src/features/leaderboards/api.ts
import { apiClient } from "../../lib/apiClient";
import { fileDownloader } from "../../lib/download";
import { ApiError } from "../../lib/errors";
import type {
  AuditEntry,
  AuditListFilter,
  Bucket,
  BucketSummary,
  CreateLeaderboardInput,
  CreateRewardInput,
  Cycle,
  DeleteImpact,
  Entry,
  GuildContributions,
  LadderDecision,
  LadderImpact,
  Leaderboard,
  ListFilter,
  PendingPackage,
  RetentionImpact,
  Reward,
  RosterComposition,
  RosterView,
  RewardsFile,
  RewardsImportMode,
  RewardsImportResult,
  RewardsPlan,
  RewardSpec,
  ScoreAdjustInput,
  StatKeyCoverage,
  UpdateLeaderboardInput,
  UpdateRewardInput,
} from "./types";

function basePath(gameId: string) {
  return `/api/admin/projects/${encodeURIComponent(gameId)}/leaderboards`;
}

/**
 * Not `basePath` + "/players" — players are a project-level resource, not a
 * leaderboard sub-resource, so this endpoint sits next to /leaderboards
 * rather than under it.
 */
function playersBasePath(gameId: string) {
  return `/api/admin/projects/${encodeURIComponent(gameId)}/players`;
}

function lbPath(gameId: string, lbId: string) {
  return `${basePath(gameId)}/${encodeURIComponent(lbId)}`;
}

function cyclePath(gameId: string, lbId: string, cycleId: string) {
  return `${lbPath(gameId, lbId)}/cycles/${encodeURIComponent(cycleId)}`;
}

function scorePath(gameId: string, lbId: string, cycleId: string, entityId: string) {
  return `${cyclePath(gameId, lbId, cycleId)}/scores/${encodeURIComponent(entityId)}`;
}

function rewardPath(gameId: string, lbId: string, rid: string) {
  return `${lbPath(gameId, lbId)}/rewards/${encodeURIComponent(rid)}`;
}

// ===== Leaderboards =====

export async function listLeaderboards(
  gameId: string,
  filter: ListFilter = {},
): Promise<Leaderboard[]> {
  const qs = new URLSearchParams();
  if (filter.status) qs.set("status", filter.status);
  if (filter.window_kind) qs.set("window_kind", filter.window_kind);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  // `leaderboards` is typed nullable because that is what a project with no
  // boards used to answer, and a cached response or an older server still can.
  // Normalizing here rather than at each call site is what keeps `null` out of
  // the pages: three of them already wrote `?? []`, and the one that did not
  // took the whole route down with it.
  const r = await apiClient.get<{ leaderboards: Leaderboard[] | null }>(
    `${basePath(gameId)}${suffix}`,
  );
  return r.data.leaderboards ?? [];
}

export async function getLeaderboard(gameId: string, lbId: string): Promise<Leaderboard> {
  const r = await apiClient.get<Leaderboard>(lbPath(gameId, lbId));
  return r.data;
}

export async function createLeaderboard(
  gameId: string,
  input: CreateLeaderboardInput,
): Promise<Leaderboard> {
  const r = await apiClient.post<Leaderboard>(basePath(gameId), input);
  return r.data;
}

export async function updateLeaderboard(
  gameId: string,
  lbId: string,
  input: UpdateLeaderboardInput,
): Promise<Leaderboard> {
  const r = await apiClient.patch<Leaderboard>(lbPath(gameId, lbId), input);
  return r.data;
}

/**
 * Archive the board — a status change, nothing more. Existing cycles and
 * scores stay readable; no new cycle or submit runs.
 *
 * Spelled out as a PATCH on purpose. Archiving used to BE the DELETE verb, and
 * DELETE now erases the board for good (`deleteLeaderboard`) — a caller left on
 * the old call would destroy exactly what it meant to preserve.
 */
export async function archiveLeaderboard(gameId: string, lbId: string): Promise<void> {
  await updateLeaderboard(gameId, lbId, { status: "archived" });
}

/**
 * Erase the board and everything under it — cycles, scores, rooms, rewards and
 * the grants nobody claimed. 204, and there is nothing to restore afterwards.
 * `getDeleteImpact` counts what that sentence covers before it is said.
 */
export async function deleteLeaderboard(gameId: string, lbId: string): Promise<void> {
  await apiClient.delete(lbPath(gameId, lbId));
}

/** What `deleteLeaderboard` would take with it, counted server-side. */
export async function getDeleteImpact(
  gameId: string,
  lbId: string,
): Promise<DeleteImpact> {
  const r = await apiClient.get<DeleteImpact>(`${lbPath(gameId, lbId)}/delete-impact`);
  return r.data;
}

/**
 * What a retention of `days` would delete on this board at the next sweep.
 * Asked before a save, not after: shortening the term burns rewards nobody
 * has claimed, and that is the one part of the change nothing can put back.
 */
export async function getRetentionImpact(
  gameId: string,
  lbId: string,
  days: number,
): Promise<RetentionImpact> {
  const qs = new URLSearchParams({ days: String(days) });
  const r = await apiClient.get<RetentionImpact>(
    `${lbPath(gameId, lbId)}/retention-impact?${qs.toString()}`,
  );
  return r.data;
}

/**
 * How many of the project's players carry `key` in their stats — the scope
 * line a player_stat guild-score board shows before it goes live ("21,904 of
 * 24,110 players carry `level`"), so a valid-but-unwritten key is caught
 * while the form is still open instead of surfacing later as an empty board.
 *
 * Server-side this is a full scan of the project's players (no index covers
 * jsonb's `?` key-exists operator), so the caller should debounce rather than
 * fire it on every keystroke — the form (not this function) owns that.
 */
export async function getStatKeyCoverage(
  gameId: string,
  key: string,
): Promise<StatKeyCoverage> {
  const qs = new URLSearchParams({ key });
  const r = await apiClient.get<StatKeyCoverage>(
    `${playersBasePath(gameId)}/stat-key-coverage?${qs.toString()}`,
  );
  return r.data;
}

// ===== Scheduled (pending) changes =====

function pendingPath(gameId: string, lbId: string) {
  return `${lbPath(gameId, lbId)}/pending`;
}

/**
 * The board's package of scheduled changes, whatever state it is in: still
 * waiting, applied at the last cut, or refused there.
 *
 * A board without one answers 404 (`pending_package_not_found`), which is not
 * an error the caller has to handle — it is the ordinary answer for the great
 * majority of boards — so it comes back as `null` rather than as a throw. Any
 * other failure still throws: "the request failed" and "there is no package"
 * are different facts and the banner draws different things for them.
 */
export async function getPendingPackage(
  gameId: string,
  lbId: string,
): Promise<PendingPackage | null> {
  try {
    const r = await apiClient.get<PendingPackage>(pendingPath(gameId, lbId));
    return r.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export interface SchedulePackageInput {
  /** Column name → the value the next cut will write. Merged into whatever the
     board already has pending, field by field. */
  fields: Record<string, unknown>;
  /** The whole prize table, `null` to leave the pending one alone. */
  rewards?: RewardSpec[] | null;
  ladder_decision?: LadderDecision;
}

/** Put the package on the board — creating it, or merging into the one that is
   already waiting. */
export async function schedulePackage(
  gameId: string,
  lbId: string,
  input: SchedulePackageInput,
): Promise<PendingPackage> {
  // `request` rather than a verb helper: PUT is this endpoint's own shape —
  // the package is one row per board, so the write is idempotent on the board
  // rather than creating a second one — and no other screen in the admin uses
  // the verb.
  const r = await apiClient.request<PendingPackage>(
    "PUT",
    pendingPath(gameId, lbId),
    input,
  );
  return r.data;
}

/** Drop the package whole. The board keeps running as it is. */
export async function cancelPackage(gameId: string, lbId: string): Promise<void> {
  await apiClient.delete(pendingPath(gameId, lbId));
}

/**
 * Take one field out of the package. Answers the package that is left, or
 * nothing at all when that field was the last one in it — an empty package is
 * deleted rather than kept, which is the same rule the CHECK constraint makes
 * of the row.
 */
export async function cancelPackageField(
  gameId: string,
  lbId: string,
  field: string,
): Promise<PendingPackage | null> {
  const r = await apiClient.delete<PendingPackage | null>(
    `${pendingPath(gameId, lbId)}/fields/${encodeURIComponent(field)}`,
  );
  // A 204 leaves `data` undefined — that is the "the package is gone" answer,
  // and the caller drops it from the cache rather than drawing an empty one.
  return r.data ?? null;
}

/**
 * Clear the trace an applied or refused package left on the page. Only ever
 * about a resolved one — a package still waiting is cancelled, not dismissed.
 */
export async function dismissPackage(gameId: string, lbId: string): Promise<void> {
  await apiClient.delete(`${pendingPath(gameId, lbId)}/resolved`);
}

/**
 * Who holds a league on this board, per rung. Read only where a package is
 * about to take the board off its ladder: the choice between keeping and
 * resetting those rows is the one scheduled change that touches players rather
 * than configuration, and it should not be made blind.
 */
export async function getLadderImpact(
  gameId: string,
  lbId: string,
): Promise<LadderImpact> {
  const r = await apiClient.get<LadderImpact>(
    `${lbPath(gameId, lbId)}/ladder-impact`,
  );
  return r.data;
}

// ===== Cycles =====

export interface ListCyclesPage {
  cycles: Cycle[];
  cursor: string;
}

export async function listCycles(
  gameId: string,
  lbId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<ListCyclesPage> {
  const qs = new URLSearchParams();
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.limit) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<ListCyclesPage>(
    `${lbPath(gameId, lbId)}/cycles${suffix}`,
  );
  return r.data;
}

export async function getCycle(
  gameId: string,
  lbId: string,
  cycleId: string,
): Promise<Cycle> {
  const r = await apiClient.get<Cycle>(cyclePath(gameId, lbId, cycleId));
  return r.data;
}

export async function listBuckets(
  gameId: string,
  lbId: string,
  cycleId: string,
): Promise<Bucket[]> {
  const r = await apiClient.get<{ buckets: Bucket[] }>(
    `${cyclePath(gameId, lbId, cycleId)}/buckets`,
  );
  return r.data.buckets;
}

/**
 * The counted form of `listBuckets`. Prefer it everywhere the screen only needs
 * "how many rooms, how full": `listBuckets` has no limit or cursor, so it costs
 * one object per bucket and a large board has thousands.
 */
export async function getBucketSummary(
  gameId: string,
  lbId: string,
  cycleId: string,
): Promise<BucketSummary> {
  const r = await apiClient.get<BucketSummary>(
    `${cyclePath(gameId, lbId, cycleId)}/buckets/summary`,
  );
  return r.data;
}

/**
 * How a personal cycle's lists were assembled — the counted answer, and the only
 * one there is: the slots are summed as the lists are written, so this costs the
 * same on a cycle of a million lists as on a cycle of one.
 *
 * 404 on a cycle that is not personal. The Cycles tab reads the cycle's own
 * STAMP before asking, so a board that switched modes last week keeps drawing
 * rooms for the weeks that were played in rooms.
 */
export async function getRosterComposition(
  gameId: string,
  lbId: string,
  cycleId: string,
): Promise<RosterComposition> {
  const r = await apiClient.get<RosterComposition>(
    `${cyclePath(gameId, lbId, cycleId)}/rosters/summary`,
  );
  return r.data;
}

/**
 * One named player's list, ranked as they see it and with the layer that filled
 * every slot — the answer to «why is my list all bots».
 *
 * A point lookup, not a filter over a listing: there is no listing of rosters
 * and there is not going to be, so the operator arrives with a player id (from
 * the players page or the search below it) rather than with a page to scroll.
 * 404 when that player has not touched the board in this cycle — reading a list
 * never creates one.
 */
export async function getPlayerRoster(
  gameId: string,
  lbId: string,
  cycleId: string,
  playerId: string,
): Promise<RosterView> {
  const r = await apiClient.get<RosterView>(
    `${cyclePath(gameId, lbId, cycleId)}/rosters/${encodeURIComponent(playerId)}`,
  );
  return r.data;
}

export interface TopResult {
  leaderboard: Leaderboard;
  cycle: Cycle;
  entries: Entry[];
  cursor: string;
}

export interface ListTopOpts {
  limit?: number;
  cursor?: string;
  bucket_id?: string;
  tier_key?: string;
  guild_id?: string;
  expand?: string[];
}

export async function listCycleTop(
  gameId: string,
  lbId: string,
  cycleId: string,
  opts: ListTopOpts = {},
): Promise<TopResult> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set("limit", String(opts.limit));
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.bucket_id) qs.set("bucket_id", opts.bucket_id);
  if (opts.tier_key) qs.set("tier_key", opts.tier_key);
  if (opts.guild_id) qs.set("guild_id", opts.guild_id);
  if (opts.expand?.length) qs.set("expand", opts.expand.join(","));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<TopResult>(
    `${cyclePath(gameId, lbId, cycleId)}/top${suffix}`,
  );
  return r.data;
}

/**
 * One team's inside of a member_contribution cycle: where the guild itself
 * stands, and how its members rank by contribution within it.
 *
 * Only that source has an inside to read — the board answers 400
 * `validation_failed` on any other — and it is the only place the contributor
 * reward table can be previewed against, since `/top` ranks the guilds.
 */
export async function getGuildContributions(
  gameId: string,
  lbId: string,
  cycleId: string,
  guildId: string,
): Promise<GuildContributions> {
  const r = await apiClient.get<GuildContributions>(
    `${cyclePath(gameId, lbId, cycleId)}/guilds/${encodeURIComponent(guildId)}/contributions`,
  );
  return r.data;
}

// ===== Score moderation =====

export async function deleteScore(
  gameId: string,
  lbId: string,
  cycleId: string,
  entityId: string,
): Promise<void> {
  await apiClient.delete(scorePath(gameId, lbId, cycleId, entityId));
}

export async function adjustScore(
  gameId: string,
  lbId: string,
  cycleId: string,
  entityId: string,
  body: ScoreAdjustInput,
): Promise<void> {
  await apiClient.patch(scorePath(gameId, lbId, cycleId, entityId), body);
}

// ===== Audit =====

export interface ListAuditPage {
  audit: AuditEntry[];
  cursor: string;
}

export async function listAudit(
  gameId: string,
  lbId: string,
  cycleId: string,
  filter: AuditListFilter = {},
): Promise<ListAuditPage> {
  const qs = new URLSearchParams();
  qs.set("cycle", cycleId);
  if (filter.entity_id) qs.set("entity", filter.entity_id);
  if (filter.reject_reason) qs.set("reject_reason", filter.reject_reason);
  if (filter.cursor) qs.set("cursor", filter.cursor);
  if (filter.limit) qs.set("limit", String(filter.limit));
  if (filter.expand?.length) qs.set("expand", filter.expand.join(","));
  const r = await apiClient.get<ListAuditPage>(
    `${lbPath(gameId, lbId)}/audit?${qs.toString()}`,
  );
  return r.data;
}

// ===== Rewards =====

export async function listRewards(gameId: string, lbId: string): Promise<Reward[]> {
  const r = await apiClient.get<{ rewards: Reward[] }>(
    `${lbPath(gameId, lbId)}/rewards`,
  );
  return r.data.rewards;
}

export async function getReward(
  gameId: string,
  lbId: string,
  rid: string,
): Promise<Reward> {
  const r = await apiClient.get<Reward>(rewardPath(gameId, lbId, rid));
  return r.data;
}

export async function createReward(
  gameId: string,
  lbId: string,
  input: CreateRewardInput,
): Promise<Reward> {
  const r = await apiClient.post<Reward>(
    `${lbPath(gameId, lbId)}/rewards`,
    input,
  );
  return r.data;
}

export async function updateReward(
  gameId: string,
  lbId: string,
  rid: string,
  input: UpdateRewardInput,
): Promise<Reward> {
  const r = await apiClient.patch<Reward>(rewardPath(gameId, lbId, rid), input);
  return r.data;
}

export async function deleteReward(
  gameId: string,
  lbId: string,
  rid: string,
): Promise<void> {
  await apiClient.delete(rewardPath(gameId, lbId, rid));
}

// ===== Rewards as a file =====

/** Body of both import endpoints: the whole file plus the mode to read it in. */
export type RewardsImportBody = { mode: RewardsImportMode } & RewardsFile;

/**
 * GET /rewards/export — download the board's rewards as a file. Goes through
 * `fileDownloader` rather than `apiClient`, which is JSON-only and would throw
 * the body away.
 *
 * Returns the filename the browser actually saved: the server's
 * Content-Disposition when it names one, else `fallbackFilename`. Pass
 * `rewardsFilename(lb.ext_key)` there so a server that omits the header still
 * produces the name the two implementations agree on.
 */
export async function exportRewards(
  gameId: string,
  lbId: string,
  fallbackFilename = "rewards.json",
): Promise<string> {
  return fileDownloader.download(
    `${lbPath(gameId, lbId)}/rewards/export`,
    fallbackFilename,
  );
}

/**
 * POST /rewards/import/plan — validate the file against this board and diff it
 * against the current rewards. Writes nothing, so it is safe to call again on
 * every mode switch.
 */
export async function planRewardsImport(
  gameId: string,
  lbId: string,
  body: RewardsImportBody,
): Promise<RewardsPlan> {
  const r = await apiClient.post<RewardsPlan>(
    `${lbPath(gameId, lbId)}/rewards/import/plan`,
    body,
  );
  return r.data;
}

/**
 * POST /rewards/import — recompute the plan server-side and apply it in one
 * transaction. The counters come back from what the transaction did, so they
 * can differ from the plan the operator saw if the board moved in between.
 */
export async function importRewards(
  gameId: string,
  lbId: string,
  body: RewardsImportBody,
): Promise<RewardsImportResult> {
  const r = await apiClient.post<RewardsImportResult>(
    `${lbPath(gameId, lbId)}/rewards/import`,
    body,
  );
  return r.data;
}

// ===== React Query keys =====

/**
 * Every filter of a game's leaderboard list — the prefix, not one view of it.
 *
 * This is the key an action on a single board answers to: which filter the
 * operator left the list on is not knowable from a board's own page, and a
 * board that was created, archived or deleted moves in or out of several of
 * those views at once.
 */
function listAllKey(gameId: string) {
  return ["leaderboards", gameId] as const;
}

export const lbKeys = {
  list: (gameId: string, filter: ListFilter) =>
    [...listAllKey(gameId), filter] as const,
  listAll: listAllKey,
  detail: (gameId: string, lbId: string) =>
    ["leaderboard", gameId, lbId] as const,
  deleteImpact: (gameId: string, lbId: string) =>
    ["leaderboard.deleteImpact", gameId, lbId] as const,
  cycles: (gameId: string, lbId: string, opts: { cursor?: string; limit?: number } = {}) =>
    ["leaderboard.cycles", gameId, lbId, opts] as const,
  cycle: (gameId: string, lbId: string, cycleId: string) =>
    ["leaderboard.cycle", gameId, lbId, cycleId] as const,
  buckets: (gameId: string, lbId: string, cycleId: string) =>
    ["leaderboard.buckets", gameId, lbId, cycleId] as const,
  bucketSummary: (gameId: string, lbId: string, cycleId: string) =>
    ["leaderboard.buckets.summary", gameId, lbId, cycleId] as const,
  /** The personal cycle's composition — bucketSummary's counterpart. */
  rosterComposition: (gameId: string, lbId: string, cycleId: string) =>
    ["leaderboard.rosters.summary", gameId, lbId, cycleId] as const,
  /** One player's list inside one cycle. */
  playerRoster: (gameId: string, lbId: string, cycleId: string, playerId: string) =>
    ["leaderboard.rosters.player", gameId, lbId, cycleId, playerId] as const,
  top: (gameId: string, lbId: string, cycleId: string, opts: ListTopOpts = {}) =>
    ["leaderboard.top", gameId, lbId, cycleId, opts] as const,
  /** One /top request per tier, fanned out and cached as a single entry. */
  topByTier: (
    gameId: string,
    lbId: string,
    cycleId: string,
    tierKeys: string[],
    limit: number,
  ) => ["leaderboard.top.byTier", gameId, lbId, cycleId, tierKeys, limit] as const,
  /**
   * The room the cycle's leading entity sits in, as the Rewards tab previews
   * it: the pair of reads that finds the room and then loads it. One key,
   * because the second read has no meaning without the first.
   */
  roomPreview: (gameId: string, lbId: string, cycleId: string, limit: number) =>
    ["leaderboard.roomPreview", gameId, lbId, cycleId, limit] as const,
  /** One team's inside of a cycle — see `getGuildContributions`. */
  guildContributions: (
    gameId: string,
    lbId: string,
    cycleId: string,
    guildId: string,
  ) =>
    ["leaderboard.guildContributions", gameId, lbId, cycleId, guildId] as const,
  audit: (gameId: string, lbId: string, cycleId: string, filter: AuditListFilter) =>
    ["leaderboard.audit", gameId, lbId, cycleId, filter] as const,
  rewards: (gameId: string, lbId: string) =>
    ["leaderboard.rewards", gameId, lbId] as const,
  reward: (gameId: string, lbId: string, rid: string) =>
    ["leaderboard.reward", gameId, lbId, rid] as const,
  /**
   * The board's one package of scheduled changes. Its own key rather than a
   * slice of `detail`: GET /leaderboards/{id} does not carry it, and a screen
   * that has no package to show must not refetch the whole board to find out.
   */
  pending: (gameId: string, lbId: string) =>
    ["leaderboard.pending", gameId, lbId] as const,
  /** Occupancy of the ladder — see `getLadderImpact`. */
  ladderImpact: (gameId: string, lbId: string) =>
    ["leaderboard.ladderImpact", gameId, lbId] as const,
};
